import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const RITUAL = [
    { t: 'Morning read — 30 min syllabus', tag: 'PREP' },
    { t: 'Two timed quizzes', tag: 'DRILL' },
    { t: 'Review yesterday\'s mistakes', tag: 'REV' },
    { t: 'One long essay outline', tag: 'WRITE' },
    { t: 'Sleep by 23:00', tag: 'REST' },
];

const QUOTES = [
    ['You do not rise to the level of your goals. You fall to the level of your systems.', '— J. Clear'],
    ['Discipline is choosing between what you want now and what you want most.', '— A. Franklin'],
    ['The margin is where the real book is written.', '— Anonymous scholar'],
];

export default function Journal() {
    const [done, setDone] = useState(new Set([0]));
    const [streak, setStreak] = useState(12);
    const [quote] = useState(QUOTES[Math.floor(Math.random() * QUOTES.length)]);

    function toggle(i) {
        const next = new Set(done);
        if (next.has(i)) next.delete(i);
        else {
            next.add(i);
            if (next.size === RITUAL.length) setStreak(streak + 1);
        }
        setDone(next);
    }

    return (
        <div data-testid="section-journal" className="space-y-10">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§IV — Daily Journal</div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)] tabular-nums">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
            </div>

            <h2 className="display text-[color:var(--ink)]" style={{ fontSize: 'clamp(40px, 5vw, 68px)' }}>
                A ritual, <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>bound</span>
                <br /> in one small book.
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
                {/* Checklist on lined paper */}
                <div className="md:col-span-2">
                    <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-3">TODAY'S RITUAL</div>
                    <div className="paper-lines pt-1 -mx-2 px-2 border-l-2 border-[color:var(--lavender-deep)]/50">
                        {RITUAL.map((r, i) => {
                            const isDone = done.has(i);
                            return (
                                <button
                                    key={i}
                                    onClick={() => toggle(i)}
                                    data-testid={`ritual-${i}`}
                                    className="w-full flex items-center gap-3 py-1.5 h-[1.75rem] text-left group"
                                >
                                    <span
                                        className={`h-4 w-4 rounded-sm border border-[color:var(--moss-deep)] flex items-center justify-center transition-all ${isDone ? 'bg-[color:var(--lavender)]' : 'bg-transparent'}`}
                                    >
                                        {isDone && <Check size={10} className="text-[color:var(--moss-dark)]" />}
                                    </span>
                                    <span
                                        className={`text-[15px] text-[color:var(--ink)] transition-all ${isDone ? 'line-through opacity-60' : ''}`}
                                    >
                                        {r.t}
                                    </span>
                                    <span className="ml-auto mono text-[9px] text-[color:var(--moss-deep)]">{r.tag}</span>
                                </button>
                            );
                        })}
                        <div className="h-[1.75rem]" />
                        <div className="h-[1.75rem]" />
                        <div className="h-[1.75rem] flex items-center">
                            <span className="display-italic text-[color:var(--moss-deep)]/50 text-sm">
                                + add another…
                            </span>
                        </div>
                    </div>
                </div>

                {/* Sidebar: streak + quote */}
                <aside className="space-y-6">
                    <div className="border border-[color:var(--moss-deep)]/40 rounded-sm p-4 bg-white/40">
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">STREAK</div>
                        <motion.div
                            key={streak}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="display-italic text-[color:var(--lavender-deep)] leading-none mt-1"
                            style={{ fontSize: 'clamp(60px, 8vw, 96px)' }}
                        >
                            {streak}
                        </motion.div>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">days in a row</div>

                        {/* Week dots */}
                        <div className="flex gap-1.5 mt-4">
                            {Array.from({ length: 14 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="h-4 flex-1 rounded-[2px]"
                                    style={{
                                        background: i < streak % 14
                                            ? 'var(--lavender-deep)'
                                            : 'rgba(59,68,30,0.15)',
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="relative pl-4 border-l-2 border-[color:var(--lavender-deep)]">
                        <p className="display-italic text-[color:var(--ink)] text-[17px] leading-snug">
                            "{quote[0]}"
                        </p>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-3">{quote[1]}</div>
                    </div>
                </aside>
            </div>

            {/* Weekly ledger */}
            <div>
                <div className="flex items-baseline justify-between mb-3">
                    <div className="mono text-[10px] text-[color:var(--moss-deep)]">WEEKLY LEDGER</div>
                    <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                        {done.size} / {RITUAL.length} today
                    </div>
                </div>
                <div className="grid grid-cols-7 gap-2">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => {
                        const v = Math.random(); // pseudo — for demo
                        const fill = i === 3 ? done.size / RITUAL.length : (i < 3 ? 0.7 + v * 0.3 : 0);
                        return (
                            <div key={i} className="text-center">
                                <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-1">{d}</div>
                                <div className="h-24 rounded-sm bg-[color:var(--moss-deep)]/10 relative overflow-hidden">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${fill * 100}%` }}
                                        transition={{ duration: 0.9, delay: i * 0.05, ease: [0.83, 0, 0.17, 1] }}
                                        className="absolute bottom-0 left-0 right-0"
                                        style={{
                                            background: i === 3 ? 'var(--lavender-deep)' : 'var(--moss-deep)',
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
